import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import durationPlugin, { Duration } from 'dayjs/plugin/duration';
import isSameOrAfterPlugin from 'dayjs/plugin/isSameOrAfter';
import isSameOrBeforePlugin from 'dayjs/plugin/isSameOrBefore';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

export const SECOND = 1000;
export const MINUTE = 60 * SECOND;
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);
dayjs.extend(quarterOfYear);
dayjs.extend(advancedFormat);
dayjs.extend(isSameOrBeforePlugin);
dayjs.extend(isSameOrAfterPlugin);
dayjs.extend(durationPlugin);

const monthNamesToNumbers: Record<string, number> = {
  January: 0,
  February: 1,
  March: 2,
  April: 3,
  May: 4,
  June: 5,
  July: 6,
  August: 7,
  September: 8,
  October: 9,
  November: 10,
  December: 11,
};

export enum DateFormats {
  DAY = 'DD',
  DAY_MONTH_YEAR = 'DD MMM YYYY',
  DAY_MONTH_YEAR_LONG = 'DD MMMM YYYY',
  DAY_MONTH_YEAR_TIME = 'DD MMM YYYY HH:mm',
  QUARTER = 'Q',
  QUARTER_YEAR = 'Q YYYY',
  YEAR = 'YYYY',
  MONTH_YEAR = 'MMM YYYY',
  MONTH_YEAR_LONG = 'MMMM YYYY',
  MONTH = 'MMM',
  MONTH_LONG = 'MMMM',
  HOUR_MINUTE_AMPM = 'hh:mm a',
  TIME_SHORT = 'HH:mm',
  TIME_SHORT_DD_MMM_YYY = 'HH:mm DD/MMM/YYYY',
  MONTH_YEAR_COMMA = 'MMM, YYYY',
  QUARTER_YEAR_COMMA = 'Q, YYYY',
  MM_DD_YYYY = 'MM/DD/YYYY',
  MM_YY = 'MM/YY',
  Q_YY = 'Q/YY',
  DD_MM_YYYY = 'DD/MM/YYYY',
  DD_MM_YYYY_TIME = 'DD/MM/YY HH-mm-ss',
  DD_MM_YYYY_TIME_SHORT = 'DD/MM/YYYY HH:mm',
  DD_MMM_YYYY = 'DD/MMM/YYYY',
  YYYY_MM_DD = 'YYYY-MM-DD',
  YYYY_MM_DD_HH_MM = 'YYYY-MM-DD_HH-mm',
  YYYY_MM_DD_TIME_SHORT = 'YYYY-MM-DD HH:mm',
  DD_MMM_YYYY_DASH = 'DD-MMM-YYYY',
  DD_MM_YYYY_HH_MM_DOT = 'DD.MM.YYYY HH.mm',
  ISO = 'YYYY-MM-DDTHH:mm:ss.SSSZ',
  YYYY_MM_DD_DOT = 'YYYY.MM.DD',
}

export enum TimeUnit {
  days = 'days',
  day = 'day',
  ms = 'ms',
  hours = 'hours',
  seconds = 'seconds',
  minutes = 'minutes',
  months = 'months',
  month = 'month',
  quarters = 'quarters',
  milliseconds = 'milliseconds',
}

export const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const isValidDate = (date?: string | null | Date, format?: string) =>
  dayjs(date, format).isValid();

interface GetNowProps {
  useUtc?: boolean;
  returnAsDate?: boolean;
}

type ReturnTypeBasedOnB<B extends boolean> = B extends true
  ? Date
  : dayjs.Dayjs;

export function getNow<B extends boolean>(
  props: GetNowProps & { returnAsDate?: B },
): ReturnTypeBasedOnB<B> {
  if (props.returnAsDate) {
    // Moment equivalent was moment.toDate();
    return new Date() as ReturnTypeBasedOnB<B>;
  } else {
    const dateObj = props.useUtc ? dayjs().utc() : dayjs();
    return dateObj as ReturnTypeBasedOnB<B>;
  }
}

export const convertDateToISO = (date: string, inputFormat?: string) => {
  const dateObj = dayjs(date, inputFormat);

  if (!dateObj.isValid()) {
    return null;
  }

  return dateObj.toISOString();
};

export const startOf = (date: string | Date, dateGroup: TimeUnit) => {
  return dayjs(date).startOf(dateGroup).toDate();
};

export const endOf = (date: string | Date, dateGroup: TimeUnit) => {
  return dayjs(date).endOf(dateGroup).toDate();
};

export const parseDate = (
  date: string,
  incomingDateFormat?: string,
  strict?: boolean,
) => dayjs(date, incomingDateFormat, strict).toDate();

export function parseTimeShortText(
  text: string | null | undefined,
): dayjs.Dayjs | null {
  const raw = (text ?? '').trim();
  if (!raw) return null;
  const parsed = dayjs(raw, DateFormats.TIME_SHORT, true);
  return parsed.isValid() ? parsed : null;
}

export const formatDate = (
  date: string | Date | dayjs.Dayjs | null | undefined,
  format: DateFormats,
  utc: boolean = true,
  fallbackString: string = '',
): string => {
  if (!date) {
    return fallbackString;
  }
  const dateObj = utc ? dayjs(date).utc() : dayjs(date);
  if (!dateObj.isValid()) {
    return fallbackString;
  }
  return dateObj.format(format) ?? fallbackString;
};

export const subtractTime = (
  amount: number,
  unit: TimeUnit,
  date: string | Date,
): Date => {
  return dayjs(date).subtract(amount, unit).toDate();
};

export const addTime = (
  amount: number,
  unit: TimeUnit,
  date: string | Date,
) => {
  return dayjs(date).add(amount, unit).toDate();
};

export const diff = (
  dateA: number | string | Date,
  dateB: number | string | Date,
  unit?: TimeUnit,
  float?: boolean,
) => {
  const dateAObj = typeof dateA === 'number' ? new Date(dateA) : dateA;
  const dateBObj = typeof dateB === 'number' ? new Date(dateB) : dateB;

  return dayjs(dateAObj).diff(dayjs(dateBObj), unit, float);
};

export const isSameTime = (
  dateA: number | string | Date,
  dateB: number | string | Date,
  unit: TimeUnit,
) => {
  return dayjs(dateA).isSame(dayjs(dateB), unit);
};

export const isAfter = (
  dateA: number | string | Date,
  dateB: number | string | Date,
) => {
  const dateAObj = typeof dateA === 'number' ? new Date(dateA) : dateA;
  const dateBObj = typeof dateB === 'number' ? new Date(dateB) : dateB;

  return dayjs(dateAObj).isAfter(dayjs(dateBObj));
};

export const isBefore = (
  dateA: number | string | Date,
  dateB: number | string | Date,
) => {
  const dateAObj = typeof dateA === 'number' ? new Date(dateA) : dateA;
  const dateBObj = typeof dateB === 'number' ? new Date(dateB) : dateB;

  return dayjs(dateAObj).isBefore(dayjs(dateBObj));
};

export const isSameOrBefore = (
  dateA: number | string | Date,
  dateB: number | string | Date,
  unit?: TimeUnit,
) => {
  return dayjs(dateA).isSameOrBefore(dateB, unit as dayjs.OpUnitType);
};

export const isSameOrAfter = (
  dateA: number | string | Date,
  dateB: number | string | Date,
  unit?: TimeUnit,
) => {
  return dayjs(dateA).isSameOrAfter(dateB, unit as dayjs.OpUnitType);
};

// Converts the date to the local timezone of the user's browser.
// Assumes date is in UTC.
export const toLocalTimezone = (
  date: string | Date = '',
  format?: string,
): string => {
  const localDate = dayjs(date).utc();
  return format ? localDate.format(format) : localDate.toString();
};

export const getDate = (
  date?: string | Date,
  isUtc: boolean = false,
): number => {
  const dateObj = dayjs(date);
  return isUtc ? dateObj.utc().date() : dateObj.date();
};

export const setDate = (
  date: number,
  originalDate?: string | Date,
  dateBoundary?: 'start' | 'end',
  isUtc: boolean = false,
): dayjs.Dayjs => {
  const dateObj = dayjs(originalDate);
  const updatedDate = isUtc ? dateObj.utc().date(date) : dateObj.date(date);
  return dateBoundary === 'start'
    ? updatedDate.startOf('day')
    : dateBoundary === 'end'
      ? updatedDate.endOf('day')
      : updatedDate;
};

export const getYear = (
  date?: string | Date,
  isUtc: boolean = false,
): number => {
  const dateObj = dayjs(date);
  return isUtc ? dateObj.utc().year() : dateObj.year();
};

export const setYear = (
  year: number,
  date?: string | Date,
  isUtc: boolean = false,
): dayjs.Dayjs => {
  const dateObj = dayjs(date);
  return isUtc ? dateObj.utc().year(year) : dateObj.year(year);
};

export const setMonth = (
  month: number | string,
  date?: string | Date,
  monthBoundary?: 'start' | 'end',
  isUtc: boolean = false,
): dayjs.Dayjs => {
  const monthNumber =
    typeof month === 'string' ? (monthNamesToNumbers[month] ?? 0) : month;
  const dateObj = dayjs(date);
  const updatedDate = isUtc
    ? dateObj.utc().month(monthNumber)
    : dateObj.month(monthNumber);
  return monthBoundary === 'start'
    ? updatedDate.startOf('month')
    : updatedDate.endOf('month');
};

/**
 * @param withoutSuffix - Whether to include the suffix (e.g., "ago").
 */
export const timeFromNow = (
  date: string | Date | number | null,
  withoutSuffix: boolean = false,
): string => {
  const dateObj = dayjs(date);
  return dateObj.fromNow(!withoutSuffix);
};

export const getDayOfWeek = (date: string | Date): number => {
  return dayjs(date).day();
};

export const createDuration = ({ seconds = 0, minutes = 0, hours = 0 }) => {
  return dayjs.duration({ seconds, minutes, hours });
};

export const subtractDuration = (
  duration: Duration,
  amount: number,
  unit: 'seconds' | 'minutes' | 'hours',
) => {
  const newDuration = duration.subtract(amount, unit);
  return newDuration.asSeconds() >= 0 ? newDuration : dayjs.duration(0);
};

export const getDurationParts = (duration: Duration) => {
  return {
    hours: duration.hours(),
    minutes: duration.minutes(),
    seconds: duration.seconds(),
  };
};
